import React from 'react';
import PropTypes from 'prop-types';
import Modal from '../Modal';
import Alert from '../Alert';
import { propertiesApi } from '../../requests/api';

// PROPERTIES columns read as SHOUTING_SNAKE; the table shows them as words.
const FIELD_LABELS = {
    NAME: 'Property Name',
    APN: 'APN',
    LEGAL_DESC: 'Legal Description',
    SQFT: 'Square Feet',
    ZIP: 'ZIP',
    LTV: 'LTV',
    DSCR: 'DSCR',
    NOI_PERCENT: 'NOI %',
    NET_OPERATING_INCOME: 'Net Operating Income',
    CAD_SITE: 'CAD Site',
    HSC_FINANCING: 'Peak Financing',
};

// The fields worth putting at the top of a duplicate merge.
const PRIORITY_FIELDS = [
    'NAME', 'ADDRESS', 'STREET', 'CITY', 'STATE', 'ZIP', 'COUNTY',
    'APN', 'LEGAL_DESC', 'SOURCE', 'TRANSACTION_TYPE', 'STATUS',
];

/*
 * The fields that are a list of rows rather than one value, so the user ticks
 * every property to take them from instead of picking one. Server-side names
 * (PropertyMergeService::MULTI_SELECT_FIELDS).
 */
const MULTI = [
    { id: 'NOTES', label: 'Notes', empty: 'no notes' },
    { id: 'CURRENT_INVESTORS', label: 'Current Investor', empty: 'no current investor' },
    { id: 'HISTORICAL_INVESTORS', label: 'Historical Investors', empty: 'no past investors' },
];

/** Mirrors PropertyMergeService::MAX_PROPERTIES. */
export const MERGE_MAX_PROPERTIES = 10;

export function plural(count, singular, pluralForm) {
    return `${count.toLocaleString()} ${count === 1 ? singular : pluralForm || singular + 's'}`;
}

/** The success line for a finished merge, from what the merge endpoint returned. */
export function mergeSummary(data) {
    const moved = Object.keys(data.rows_moved || {}).reduce((sum, table) => sum + data.rows_moved[table], 0);
    const collected = data.collected || {};
    const kept = [
        collected.notes ? plural(collected.notes, 'note') : null,
        collected.current_ownerships ? plural(collected.current_ownerships, 'current owner') : null,
        collected.historical_ownerships ? plural(collected.historical_ownerships, 'past owner') : null,
    ].filter(Boolean);

    return `Merged ${plural(data.merged_ids.length, 'property', 'properties')} into ${data.primary_id}.`
        + (data.fields_changed.length
            ? ` ${plural(data.fields_changed.length, 'field')} updated.`
            : ' No field values changed.')
        + (kept.length ? ` Kept ${kept.join(', ')}.` : '')
        + (moved ? ` ${plural(moved, 'related record')} moved.` : '');
}

function dateUS(value) {
    const parts = /^(\d{4})-(\d{2})-(\d{2})/.exec(value || '');
    return parts ? `${parts[2]}/${parts[3]}/${parts[1]}` : '';
}

/** A short description of what one property would contribute to a field. */
function summarise(fieldId, entries) {
    if (!entries || !entries.length) return null;

    if (fieldId === 'NOTES') {
        return entries.map((note) => {
            const when = dateUS(note.date);
            const text = note.text.replace(/\s+/g, ' ').trim();
            return {
                short: (when ? `${when} · ` : '') + (text.length > 90 ? text.slice(0, 90) + '…' : text),
                full: (when ? `${when}: ` : '') + text,
            };
        });
    }

    return entries.map((owner) => {
        const span = [dateUS(owner.start_date), dateUS(owner.end_date)].filter(Boolean).join(' – ');
        const line = owner.name + (span ? ` (${span})` : '');
        return { short: line, full: line };
    });
}

function fieldLabel(field) {
    if (FIELD_LABELS[field]) return FIELD_LABELS[field];

    return field
        .toLowerCase()
        .split('_')
        .map((word) => (word.length <= 3 && word !== 'the' ? word.toUpperCase() : word.charAt(0).toUpperCase() + word.slice(1)))
        .join(' ');
}

function orderFields(fields) {
    const rank = (field) => {
        const priority = PRIORITY_FIELDS.indexOf(field.field);
        return priority === -1 ? PRIORITY_FIELDS.length : priority;
    };

    return fields.slice().sort((a, b) => rank(a) - rank(b));
}

function describe(property) {
    return [property.address, property.city, property.state].filter(Boolean).join(', ') || '—';
}

export default function MergeProperties({ open, properties, onClose, onMerged }) {
    // 'select' picks the records and the primary; 'fields' is the comparison.
    const [step, setStep] = React.useState('select');
    const [includedIds, setIncludedIds] = React.useState([]);
    const [primaryId, setPrimaryId] = React.useState(null);
    const [comparison, setComparison] = React.useState(null);
    // field => the id of the property whose value wins.
    const [choices, setChoices] = React.useState({});
    // Multi-select field => the ids to take rows from. The primary is always
    // in it: a merge is not a way to lose the kept record's own notes.
    const [selections, setSelections] = React.useState({});
    const [onlyDifferences, setOnlyDifferences] = React.useState(true);
    const [busy, setBusy] = React.useState(false);
    const [error, setError] = React.useState(null);

    React.useEffect(() => {
        if (!open) return;
        const ids = (properties || []).map((property) => property.id);
        setStep('select');
        setIncludedIds(ids);
        setPrimaryId(ids[0] === undefined ? null : ids[0]);
        setComparison(null);
        setChoices({});
        setSelections({});
        setOnlyDifferences(true);
        setBusy(false);
        setError(null);
    }, [open, properties]);

    if (!open) return null;

    const included = (properties || []).filter((property) => includedIds.indexOf(property.id) !== -1);
    // Find Dups sends each property's account numbers; the list's own rows do
    // not carry them, and an empty column there would read as "none".
    const showAccount = (properties || []).some((property) => property.account_number !== undefined);

    const toggleIncluded = (id) => {
        const next = includedIds.indexOf(id) === -1
            ? includedIds.concat([id])
            : includedIds.filter((entry) => entry !== id);

        setIncludedIds(next);
        // The primary has to be one of the records being merged.
        if (next.indexOf(primaryId) === -1) setPrimaryId(next[0] === undefined ? null : next[0]);
        setError(null);
    };

    const loadComparison = () => {
        if (included.length < 2) {
            setError('Pick at least two properties to merge.');
            return;
        }

        // Primary first: it is the second column, and the order of the rest is
        // the order of the columns after it.
        const ordered = [primaryId].concat(includedIds.filter((id) => id !== primaryId));

        setBusy(true);
        setError(null);

        propertiesApi.compareDuplicates(ordered)
            .then((data) => {
                const defaults = {};
                data.fields.forEach((row) => {
                    defaults[row.field] = primaryId;
                });

                // The primary only, matching the radio fields: anything from
                // another record is something the user asks for.
                const picked = {};
                MULTI.forEach((field) => {
                    picked[field.id] = [primaryId];
                });

                setComparison(data);
                setChoices(defaults);
                setSelections(picked);
                setStep('fields');
                setBusy(false);
            })
            .catch((err) => {
                setBusy(false);
                setError(err.message || 'Could not compare these properties.');
            });
    };

    const apply = () => {
        const mergeIds = includedIds.filter((id) => id !== primaryId);

        setBusy(true);
        setError(null);

        propertiesApi.mergeDuplicates(primaryId, mergeIds, choices, selections)
            .then((data) => {
                setBusy(false);
                onMerged(data);
            })
            .catch((err) => {
                setBusy(false);
                setError(err.message || 'Could not merge these properties.');
            });
    };

    const columns = comparison ? comparison.properties : [];
    const rows = comparison
        ? orderFields(comparison.fields).filter((row) => !onlyDifferences || row.differs)
        : [];
    const differingCount = comparison ? comparison.fields.filter((row) => row.differs).length : 0;

    return (
        <Modal
            show={open}
            onClose={busy ? () => {} : onClose}
            className={`MergeProps ${step === 'fields' ? 'is-comparing' : ''}`}
        >
            <div className="MergeProps_body">
                <h2 className="MergeProps_title">
                    {step === 'select' ? 'Merge Properties · Step 1 of 2' : 'Merge Properties · Step 2 of 2'}
                </h2>

                {step === 'select' ? (
                    <p className="MergeProps_intro">
                        Tick the properties to merge together, and choose which one is the primary.
                        The primary is the record that is kept.
                    </p>
                ) : (
                    <p className="MergeProps_intro">
                        For each field, choose which property&apos;s value the primary should keep.
                        The primary&apos;s own values are selected by default. Notes and investors
                        are tick boxes, so you can keep them from more than one property.
                    </p>
                )}

                {error ? <Alert type="error">{error}</Alert> : null}

                {step === 'select' ? (
                    <div className="MergeProps_scroll MergeProps_scroll--fit">
                        <table className="MergeProps_table MergeProps_select">
                            <thead>
                                <tr>
                                    <th>Merge</th>
                                    <th>Primary</th>
                                    <th>ID</th>
                                    <th>Name</th>
                                    <th>Address</th>
                                    {showAccount ? <th>Account #</th> : null}
                                    <th>Source</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(properties || []).map((property) => {
                                    const isIncluded = includedIds.indexOf(property.id) !== -1;

                                    return (
                                        <tr key={property.id} className={isIncluded ? '' : 'is-excluded'}>
                                            <td>
                                                <input
                                                    type="checkbox"
                                                    checked={isIncluded}
                                                    disabled={busy}
                                                    aria-label={`Merge property ${property.id}`}
                                                    onChange={() => toggleIncluded(property.id)}
                                                />
                                            </td>
                                            <td>
                                                <input
                                                    type="radio"
                                                    name="merge-primary"
                                                    checked={primaryId === property.id}
                                                    disabled={busy || !isIncluded}
                                                    aria-label={`Make property ${property.id} the primary`}
                                                    onChange={() => setPrimaryId(property.id)}
                                                />
                                            </td>
                                            <td>{property.id}</td>
                                            <td>{property.name || ''}</td>
                                            <td>{describe(property)}</td>
                                            {showAccount ? <td>{property.account_number || ''}</td> : null}
                                            <td>{property.source || ''}</td>
                                            <td>{property.status || ''}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <React.Fragment>
                        <label className="MergeProps_toggle">
                            <input
                                type="checkbox"
                                checked={onlyDifferences}
                                disabled={busy}
                                onChange={(event) => setOnlyDifferences(event.target.checked)}
                            />
                            <span>
                                {`Only show the ${differingCount.toLocaleString()} field${differingCount === 1 ? '' : 's'} that differ`}
                            </span>
                        </label>

                        <div className="MergeProps_scroll">
                            <table className="MergeProps_table MergeProps_compare">
                                <thead>
                                    <tr>
                                        <th>Field</th>
                                        {columns.map((property, index) => (
                                            <th key={property.id}>
                                                <span className="MergeProps_col_id">
                                                    {index === 0 ? `Primary · ${property.id}` : `#${property.id}`}
                                                </span>
                                                <span className="MergeProps_col_name">{property.name || describe(property)}</span>
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {/*
                                      * Notes and investors first: they are
                                      * lists rather than values, so they are
                                      * ticked per property and several can be
                                      * kept at once.
                                      */}
                                    {comparison.collections ? MULTI.map((field) => (
                                        <tr key={field.id} className="is-multi">
                                            <th scope="row">
                                                {field.label}
                                                <span className="MergeProps_multi_hint">keep from any</span>
                                            </th>
                                            {columns.map((property, index) => {
                                                const entries = (comparison.collections[field.id] || {})[String(property.id)] || [];
                                                const lines = summarise(field.id, entries);
                                                const chosen = (selections[field.id] || []).indexOf(property.id) !== -1;

                                                return (
                                                    <td key={property.id}>
                                                        <label className="MergeProps_value">
                                                            <input
                                                                type="checkbox"
                                                                checked={chosen}
                                                                // The primary's own rows are already
                                                                // on the record being kept.
                                                                disabled={busy || index === 0 || !entries.length}
                                                                aria-label={`${field.label} from property ${property.id}`}
                                                                onChange={(event) => {
                                                                    const current = selections[field.id] || [];
                                                                    setSelections({
                                                                        ...selections,
                                                                        [field.id]: event.target.checked
                                                                            ? current.concat([property.id])
                                                                            : current.filter((id) => id !== property.id),
                                                                    });
                                                                }}
                                                            />
                                                            <span className={lines ? '' : 'MergeProps_blank'}>
                                                                {lines
                                                                    ? lines.map((line, i) => (
                                                                        <span className="MergeProps_multi_line" key={i} title={line.full}>{line.short}</span>
                                                                    ))
                                                                    : field.empty}
                                                            </span>
                                                        </label>
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    )) : null}
                                    {rows.length === 0 ? (
                                        <tr>
                                            <td colSpan={columns.length + 1} className="MergeProps_empty">
                                                These properties hold the same value for every field.
                                            </td>
                                        </tr>
                                    ) : null}
                                    {rows.map((row) => (
                                        <tr key={row.field} className={row.differs ? 'is-differing' : ''}>
                                            <th scope="row">{fieldLabel(row.field)}</th>
                                            {columns.map((property) => {
                                                const value = row.values[String(property.id)];

                                                return (
                                                    <td key={property.id}>
                                                        <label className="MergeProps_value">
                                                            <input
                                                                type="radio"
                                                                name={`merge-field-${row.field}`}
                                                                checked={choices[row.field] === property.id}
                                                                disabled={busy}
                                                                aria-label={`${fieldLabel(row.field)} from property ${property.id}`}
                                                                onChange={() =>
                                                                    setChoices({ ...choices, [row.field]: property.id })}
                                                            />
                                                            <span
                                                                className={value === null ? 'MergeProps_blank' : ''}
                                                                title={value || ''}
                                                            >
                                                                {value === null ? 'blank' : value}
                                                            </span>
                                                        </label>
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </React.Fragment>
                )}

                <div className="MergeProps_actions">
                    {step === 'fields' ? (
                        <span className="MergeProps_note">
                            {`The other ${includedIds.length - 1} propert${includedIds.length - 1 === 1 ? 'y is' : 'ies are'} deleted. `}
                            {'Loan quotes, proposals and the rest move to the primary; notes and investors only where ticked.'}
                        </span>
                    ) : null}
                    <button
                        type="button"
                        className={`button-new text ${busy ? 'inactive' : ''}`}
                        onClick={step === 'fields' ? () => setStep('select') : onClose}
                        disabled={busy}
                    >
                        {step === 'fields' ? 'Back' : 'Cancel'}
                    </button>
                    <button
                        type="button"
                        className={`button-new positive ${busy || included.length < 2 ? 'inactive' : ''}`}
                        onClick={step === 'select' ? loadComparison : apply}
                        disabled={busy || included.length < 2}
                    >
                        {busy
                            ? (step === 'select' ? 'Comparing…' : 'Merging…')
                            : (step === 'select'
                                ? `Compare ${included.length} properties`
                                : `Merge into ${primaryId}`)}
                    </button>
                </div>
            </div>
        </Modal>
    );
}

MergeProperties.propTypes = {
    open: PropTypes.bool.isRequired,
    // A Find Dups group, or the rows ticked on the properties list.
    properties: PropTypes.array,
    onClose: PropTypes.func.isRequired,
    // Called with { primary_id, merged_ids, fields_changed, rows_moved }.
    onMerged: PropTypes.func.isRequired,
};
